import { clsx } from 'clsx';
import { CSSProperties } from 'react';

import styles from './ColorPalette.module.scss';
import { CheckmarkIcon } from './Icons';

/**
 * Props for `<ColorPalette>`.
 *
 * Required: `colors`, `onSelect`. Used inside the legend more-menu's
 * "Change color" submenu, but exported as a primitive too.
 */
export interface ColorPaletteProps {
  /** Swatch colours to render. Any CSS colour string. */
  colors: string[];
  /** Currently selected colour — gets the checkmark overlay. */
  selectedColor?: string;
  /** Called with the picked colour and its index in `colors`. */
  onSelect: (color: string, index: number) => void;
  /** Extra class name forwarded to the palette root. */
  className?: string;
  /** Test selector forwarded as `data-testid`. */
  'data-testid'?: string;
}

/**
 * Grid of clickable colour swatches used by chartv2 to recolour a
 * series. Wraps each colour in a square button and overlays a checkmark
 * on the currently-selected swatch.
 *
 * Most consumers reach this through the legend's "Change colour"
 * submenu, but it's exported as a primitive too.
 */
export const ColorPalette = ({
  colors,
  selectedColor,
  onSelect,
  className,
  'data-testid': testId = 'chartv2-color-palette',
}: ColorPaletteProps) => (
  <div className={clsx(styles.root, className)} data-testid={testId}>
    {colors.map((color, i) => {
      const isSelected = color === selectedColor;

      return (
        <button
          key={`${color}-${i}`}
          type="button"
          className={clsx(styles.swatch, isSelected && styles.selected)}
          style={
            {
              backgroundColor: color,
              '--chartv2-palette-swatch-color': color,
            } as CSSProperties
          }
          onClick={() => onSelect(color, i)}
          aria-label={color}
          aria-pressed={isSelected}
          data-testid={`${testId}-${i}`}
        >
          {isSelected && (
            <span className={styles.check} aria-hidden>
              <CheckmarkIcon />
            </span>
          )}
        </button>
      );
    })}
  </div>
);
